import APIKit from "../Allapis/Apikit/apikit";

const BASE = "/admin/analytics/usage";

const DEFAULT_RETRY_SECONDS = 15;
const DEFAULT_MAX_POLL_ATTEMPTS = 40;

export { DEFAULT_MAX_POLL_ATTEMPTS, DEFAULT_RETRY_SECONDS };

export class AnalyticsPollCancelledError extends Error {
  constructor() {
    super("Analytics poll cancelled");
    this.name = "AnalyticsPollCancelledError";
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pollUntilReady = async (fetchFn, options = {}) => {
  const {
    onBuilding,
    isCancelled,
    maxAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
    defaultRetrySeconds = DEFAULT_RETRY_SECONDS,
  } = options;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (isCancelled?.()) throw new AnalyticsPollCancelledError();

    const res = await fetchFn();
    if (!res?.loading) return res;

    onBuilding?.({
      meta: res?.meta,
      attempt: attempt + 1,
      maxAttempts,
      retryAfterSeconds: res?.meta?.retry_after_seconds ?? defaultRetrySeconds,
    });

    const retrySeconds = res?.meta?.retry_after_seconds ?? defaultRetrySeconds;
    await sleep(retrySeconds * 1000);

    if (isCancelled?.()) throw new AnalyticsPollCancelledError();
  }

  throw new Error(
    "Analytics dashboard is taking longer than expected. Please try again in a few minutes.",
  );
};

const get = async (path, params = {}) => {
  const res = await APIKit.get(`${BASE}${path}`, { params });
  return res.data;
};

export const getUsageOverview = (params) => get("/overview", params);
export const getUsageActiveUsers = (params) => get("/active-users", params);
export const getUsagePageViews = (params) => get("/page-views", params);
export const getUsageProducts = (params) => get("/products", params);
export const getUsageScreenTime = (params) => get("/screen-time", params);
export const getUsageDropOffs = (params) => get("/drop-offs", params);
export const getUsageEngagementFunnel = (params) =>
  get("/funnels/engagement", params);
export const getUsageInvestmentFunnel = (params) =>
  get("/funnels/investment", params);
export const getUsageAuthFunnel = (params) => get("/funnels/auth", params);
export const getUsageRetention = (params) => get("/retention", params);
export const getUsageAuthSummary = (params) => get("/auth-summary", params);
export const getUsageDashboard = (params, options) =>
  pollUntilReady(() => get("/dashboard", params), options);
export const getUsageCampaignRoi = (params) => get("/campaign-roi", params);
export const getUsageHotLeads = (params) => get("/hot-leads", params);
export const getConnectedAnalyticsUsers = (params) => get("/connected-users", params);
export const getUserAnalyticsDashboard = (userId, params) =>
  get(`/users/${userId}`, params);
export const getUsageWidgetDetail = (params) => get("/widget-detail", params);
export const getUsageGeoMap = (params) => get("/geo-map", params);
export const getUsageGeoCountryUsers = (params) => get("/geo-map/users", params);
export const getUsageDevices = (params) => get("/devices", params);
export const getUsageDeviceBrandUsers = (params) => get("/devices/users", params);

export const resyncAnalyticsMappings = (params = {}) =>
  APIKit.post(`${BASE}/resync`, params);
